import React, { useState, useRef } from 'react';
import { Upload, X, Image, FileText, Download, Trash2 } from 'lucide-react';

interface MediaFile {
  id: string;
  name: string;
  type: 'photo' | 'document';
  url: string;
  uploadDate: string;
  size?: number;
}

interface MediaManagerProps {
  personId: string;
  personName: string;
  photos: string[];
  documents: string[];
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (photos: string[], documents: string[]) => void;
}

const MediaManager: React.FC<MediaManagerProps> = ({
  personId: _personId,
  personName,
  photos,
  documents,
  isOpen,
  onClose,
  onUpdate
}) => {
  // Note: _personId will be used for file organization in future iterations
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Convert string arrays to MediaFile objects for display
  const photoFiles: MediaFile[] = photos.map((url, index) => ({
    id: `photo-${index}`,
    name: `Photo ${index + 1}`,
    type: 'photo',
    url,
    uploadDate: new Date().toISOString()
  }));

  const documentFiles: MediaFile[] = documents.map((url, index) => ({
    id: `doc-${index}`,
    name: `Document ${index + 1}`,
    type: 'document',
    url,
    uploadDate: new Date().toISOString()
  }));

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
  };

  const handleFiles = (files: File[]) => {
    const newPhotos = [...photos];
    const newDocuments = [...documents];

    files.forEach(file => {
      // In a real app, you'd upload to a server and get back URLs
      // For now, we'll simulate with placeholder URLs
      const fakeUrl = `upload://${file.name}`;
      
      if (file.type.startsWith('image/')) {
        newPhotos.push(fakeUrl);
      } else {
        newDocuments.push(fakeUrl);
      }
    });

    onUpdate(newPhotos, newDocuments);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onUpdate(newPhotos, documents);
  };

  const removeDocument = (index: number) => {
    const newDocuments = documents.filter((_, i) => i !== index);
    onUpdate(photos, newDocuments);
  };

  const getFileIcon = (file: MediaFile) => {
    if (file.type === 'photo') {
      return <Image className="h-8 w-8 text-blue-500" />;
    }
    return <FileText className="h-8 w-8 text-green-500" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">
            Media for {personName}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 transition-colors ${
              dragOver ? 'border-ocean-teal bg-ocean-blue bg-opacity-10' : 'border-gray-300 hover:border-ocean-teal'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              Drop files here or click to upload
            </h3>
            <p className="text-gray-500 mb-4">
              Upload photos (JPG, PNG) and documents (PDF, DOC, TXT)
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn-primary"
            >
              Choose Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Photos */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Image className="h-5 w-5 mr-2 text-blue-500" />
                Photos ({photoFiles.length})
              </h3>
              
              {photoFiles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No photos uploaded yet
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {photoFiles.map((photo, index) => (
                    <div key={photo.id} className="relative group">
                      <div className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                        {photo.url.startsWith('upload://') ? (
                          <div className="text-center">
                            <Image className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                            <p className="text-xs text-gray-500">{photo.name}</p>
                          </div>
                        ) : (
                          <img
                            src={photo.url}
                            alt={photo.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        )}
                      </div>
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 bg-ocean-teal text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Documents */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-green-500" />
                Documents ({documentFiles.length})
              </h3>
              
              {documentFiles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No documents uploaded yet
                </div>
              ) : (
                <div className="space-y-3">
                  {documentFiles.map((doc, index) => (
                    <div key={doc.id} className="flex items-center p-3 bg-gray-50 rounded-lg group">
                      {getFileIcon(doc)}
                      <div className="flex-1 ml-3">
                        <h4 className="font-medium text-gray-700">{doc.name}</h4>
                        <p className="text-sm text-gray-500">
                          Uploaded {new Date(doc.uploadDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => removeDocument(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Upload Tips */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Upload Tips</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Photos: JPEG, PNG files up to 10MB each</li>
              <li>• Documents: PDF, Word, or text files up to 25MB each</li>
              <li>• You can drag and drop multiple files at once</li>
              <li>• Consider scanning old photos at 300 DPI for best quality</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex justify-end mt-6 pt-4 border-t">
            <button
              onClick={onClose}
              className="btn-primary"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaManager;
